import { useQuery } from '@tanstack/react-query';
import { API_CONFIG } from '@/lib/api/config';
import { useAuth } from '@/components/AuthProvider';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@/components/AuthProvider';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';

interface FeatureLimitCheck {
  allowed: boolean;
  limit: number | null;
  usage: number;
  remaining: number | null;
  message?: string;
}

interface OrganizationEntitlements {
  organizationId: string;
  planId: string | null;
  planName: string;
  subscriptionId: string | null;
  subscriptionStatus: string;
  entitlements: {
    limits: Record<string, number>;
    features: Record<string, boolean>;
    sub_features?: Record<string, Record<string, number>>;
  };
}

// Features the app always enables regardless of plan (UI chrome + support links).
// Kept as a module constant so both the success and fallback branches reference
// the same set without copy-paste drift.
//
// 2026-05-14: removed `meta_ads: true`. It was a hardcoded "always on" flag
// that silently overrode every plan/override gate on `features.meta_ads`,
// because consumers spread this map AFTER the backend entitlements
// (`{ ...ent.features, ...ALWAYS_ENABLED_FEATURES }`). The backend could
// correctly report `features.meta_ads = false` from an override and this
// line would flip it back to true client-side, leaving Meta Ads visible
// in the sidebar regardless. Meta Ads is a paid feature and must respect
// the entitlement engine like LinkedIn Ads and TikTok Ads do. Do not add
// it back without simultaneously removing the corresponding sidebar/route
// gates that read `features.meta_ads`.
export const ALWAYS_ENABLED_FEATURES = {
  inbox: true,
  scheduling_posts: true,
  lead_ads_sync: true,
  whatsapp_ads: true,
  calendly: true,
  segments: true,
  chat_history: true,
  message_reports: true,
  campaign_performance: true,
  ads_results: true,
  sales_and_conversions: true,
  billing: true,
  how_to_use: true,
  help_articles: true,
  raise_ticket: true,
  contact_support: true,
};

/**
 * Hook to get organization entitlements (features and limits).
 *
 * Previously this fired its own `fetch('/subscriptions/current')` with a
 * distinct query key (`['organization-entitlements', orgId]`), which meant
 * every page load made TWO identical HTTP calls — one for the subscription
 * state, one for entitlements (same endpoint, different consumer). Now it
 * reads from the same shared `useCurrentSubscription()` cache and derives
 * the entitlements shape client-side via useMemo. Zero extra network.
 *
 * The backend's `/subscriptions/current` response embeds `entitlements`
 * (and `plan`) on the subscription object, so no new endpoint call is
 * needed. See the Subscription type in services/subscriptions.ts.
 */
export function useOrganizationEntitlements() {
  // Safely get auth context - check if context exists without throwing
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;
  const organizationId = user?.organization_id;

  const query = useCurrentSubscription({ enabled: !!organizationId });

  const entitlements = useMemo<OrganizationEntitlements | undefined>(() => {
    if (!organizationId) return undefined;
    const sub: any = query.data;

    // Backend returned full entitlements — merge with always-on overrides.
    if (sub?.entitlements?.entitlements || sub?.entitlements?.limits) {
      // Two possible shapes the backend has used:
      //   (a) sub.entitlements = { limits, features, sub_features }       ← flat
      //   (b) sub.entitlements = { entitlements: { limits, features... } } ← wrapped
      const ent = sub.entitlements.entitlements || sub.entitlements;
      return {
        organizationId,
        planId: sub.plan?.id || sub.plan_id || null,
        planName: sub.plan?.name || 'Subscription',
        subscriptionId: sub.id || null,
        subscriptionStatus: sub.status || 'active',
        entitlements: {
          limits: ent.limits || {},
          features: { ...ent.features, ...ALWAYS_ENABLED_FEATURES },
          sub_features: ent.sub_features || {},
        },
      };
    }

    // Fallback: no subscription / no entitlements — restrictive defaults,
    // only the always-on UI features enabled. Matches prior behaviour.
    return {
      organizationId,
      planId: sub?.plan?.id || sub?.plan_id || null,
      planName: sub?.plan?.name || 'No Subscription',
      subscriptionId: sub?.id || null,
      subscriptionStatus: sub?.status || 'none',
      entitlements: {
        limits: {},
        features: { ...ALWAYS_ENABLED_FEATURES },
      },
    };
  }, [query.data, organizationId]);

  // Mirror the React Query return shape consumers expect (data / isLoading /
  // error / refetch / etc.), but override `data` with the derived entitlements.
  return {
    ...query,
    data: entitlements,
  } as ReturnType<typeof useQuery<OrganizationEntitlements>> & { data: OrganizationEntitlements | undefined };
}

/**
 * Hook to check if a feature is enabled
 */
export function useCanUseFeature(featureName: string) {
  const { data: entitlements } = useOrganizationEntitlements();

  const isEnabled = entitlements?.entitlements.features[featureName] === true;
  const limit = entitlements?.entitlements.limits[featureName] ??
    entitlements?.entitlements.limits[mapFeatureToLimitKey(featureName)];

  return {
    canUse: isEnabled && (limit === -1 || (limit !== null && limit !== undefined && limit > 0)),
    limit,
    isEnabled,
  };
}

/**
 * Map feature name to limit key
 */
function mapFeatureToLimitKey(featureName: string): string {
  const map: Record<string, string> = {
    contacts: 'contacts',
    templates: 'templates',
    campaigns: 'campaigns',
    whatsapp_flows: 'whatsapp_flows',
    automations: 'automation_flows',
    business_accounts: 'business_accounts',
    analytics: 'analytics_level',
    commerce: 'commerce',
    integrations: 'integrations',
    messages_per_month: 'messages_per_month',
    ai_generation: 'ai_generation',
    leads: 'leads',
    notes_and_tags: 'notes_and_tags',
    instagram_automation: 'instagram_automation',
    appointments: 'appointments',
    data_store: 'data_store',
  };
  return map[featureName] || featureName;
}

/**
 * Hook to get current usage count for a feature
 * This is a helper that fetches current usage from the backend
 */
export function useFeatureUsage(featureName: string, businessAccountId?: string) {
  // Safely get auth context - check if context exists without throwing
  const authContext = useContext(AuthContext);
  const organizationId = authContext?.user?.organization_id;

  return useQuery<{ count: number; limit: number | null }>({
    queryKey: ['feature-usage', featureName, organizationId, businessAccountId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const params = new URLSearchParams({
        feature: featureName,
        organizationId,
      });

      if (businessAccountId) {
        params.append('businessAccountId', businessAccountId);
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/admin/organizations/${organizationId}/usage?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch feature usage');
      }

      const result = await response.json();
      return result.data;
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
  });
}

/**
 * Helper function to extract error message from API error response
 * Handles limit exceeded errors specifically
 */
export function getLimitErrorMessage(error: any): string {
  // Handle axios-style errors (if using axios)
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  // Handle API client error format (from our ApiResponse)
  if (error?.error && typeof error.error === 'string') {
    return error.error;
  }
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  // Handle Error objects
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred. Please try again.';
}

/**
 * Helper function to check if error is a limit exceeded error
 */
export function isLimitExceededError(error: any): boolean {
  const errorMessage = getLimitErrorMessage(error).toLowerCase();
  return (
    errorMessage.includes('limit reached') ||
    errorMessage.includes('limit exceeded') ||
    errorMessage.includes('not available on your current plan') ||
    errorMessage.includes('upgrade') ||
    errorMessage.includes('feature is not enabled')
  );
}

